import { useState, useEffect, useRef } from "react";
import { Trophy, BarChart3, Target, Lock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

function getScoreColor(score) {
  if (score >= 80) return "#22c55e";
  if (score >= 60) return "#eab308";
  if (score >= 40) return "#f97316";
  return "#ef4444";
}

function getScoreBg(score) {
  if (score >= 80) return "#f0fdf4";
  if (score >= 60) return "#fefce8";
  if (score >= 40) return "#fff7ed";
  return "#fef2f2";
}

function photoUrl(ref) {
  if (!ref) return null;
  return `${API}/api/places/photo?maxwidth=96&photo_reference=${encodeURIComponent(ref)}`;
}

/* ── Avatar with photo or fallback ── */
function BizAvatar({ name, photoRef, size = 48 }) {
  const [imgErr, setImgErr] = useState(false);
  const url = photoUrl(photoRef);
  const letter = (name || "?")[0].toUpperCase();
  const colors = ["#3b82f6", "#8b5cf6", "#ec4899", "#f97316", "#10b981", "#6366f1"];
  const bg = colors[letter.charCodeAt(0) % colors.length];

  if (url && !imgErr) {
    return (
      <img
        src={url}
        alt={name}
        onError={() => setImgErr(true)}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", border: "2px solid #fff", boxShadow: "0 1px 4px rgba(0,0,0,0.1)" }}
      />
    );
  }
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: bg, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: size * 0.4, border: "2px solid #fff", boxShadow: "0 1px 4px rgba(0,0,0,0.1)" }}>
      {letter}
    </div>
  );
}

/* ── Animated score bar ── */
function ScoreBar({ score, animated }) {
  const color = getScoreColor(score);
  return (
    <div style={{ flex: 1, height: 8, background: "#e5e7eb", borderRadius: 4, overflow: "hidden" }}>
      <div style={{
        height: "100%",
        width: animated ? `${score}%` : "0%",
        background: color,
        borderRadius: 4,
        transition: "width 0.8s ease-out",
      }} />
    </div>
  );
}

/* ── Single leaderboard row ── */
function LeaderRow({ entry, rank, myScore, index, isLeader }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const isYou = entry.is_you;
  const gap = entry.total - myScore;

  return (
    <div
      ref={ref}
      data-testid={`competitor-row-${isYou ? "you" : index}`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "14px 12px",
        background: isYou ? "#eff6ff" : "#fff",
        borderLeft: isYou ? "4px solid #3b82f6" : "4px solid transparent",
        borderBottom: "1px solid #f3f4f6",
        borderRadius: 10,
        opacity: visible ? 1 : 0,
        transform: visible ? "translateX(0)" : "translateX(-20px)",
        transition: `opacity 0.4s ease ${index * 150}ms, transform 0.4s ease ${index * 150}ms`,
        animation: isYou && visible ? "pulseOnce 1s ease-in-out" : "none",
      }}
    >
      {/* Rank */}
      <div style={{ width: 24, textAlign: "center", fontWeight: 800, fontSize: 14, color: isYou ? "#3b82f6" : "#6b7280", flexShrink: 0 }}>
        {isYou ? "—" : `#${rank}`}
      </div>

      {/* Photo */}
      <BizAvatar name={entry.name} photoRef={entry.photo_reference} size={40} />

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 800, fontSize: 14, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
            {entry.name}
          </span>
          {isYou && (
            <span style={{ fontSize: 9, fontWeight: 800, background: "#3b82f6", color: "#fff", padding: "2px 6px", borderRadius: 4, whiteSpace: "nowrap" }}>
              YOU
            </span>
          )}
          {isLeader && !isYou && (
            <span style={{ fontSize: 9, fontWeight: 800, background: "#f59e0b", color: "#fff", padding: "2px 6px", borderRadius: 4, whiteSpace: "nowrap" }}>
              #1
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: "#6b7280" }}>
          {entry.review_count?.toLocaleString() || 0} reviews &middot; {entry.rating}&#9733;
        </div>
        {!isYou && (
          <div style={{ fontSize: 11, fontWeight: 600, color: gap > 0 ? "#dc2626" : "#059669", marginTop: 2 }}>
            {gap > 0 ? `+${gap} pts ahead` : `${gap} pts behind`}
          </div>
        )}
      </div>

      {/* Score */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
        <span style={{ fontWeight: 800, fontSize: 16, color: getScoreColor(entry.total), minWidth: 28, textAlign: "right" }}>
          {entry.total}
        </span>
      </div>
    </div>
  );
}

/* ── Gap analysis cards ── */
function GapCards({ competitors, dimensions, myScores, hasAccess, onUnlock }) {
  const you = competitors.find(c => c.is_you);
  const others = competitors.filter(c => !c.is_you);
  if (!you || others.length === 0) return null;

  const sorted = [...others].sort((a, b) => b.total - a.total);
  const leader = sorted[0];
  const leaderGap = leader.total - you.total;
  const yourRank = [...competitors].sort((a, b) => b.total - a.total).findIndex(c => c.is_you) + 1;

  // Find biggest gap by dimension
  let biggestGapDim = dimensions[0] || "N/A";
  let biggestGapVal = 0;
  dimensions.forEach((dim, i) => {
    const avgComp = others.reduce((s, c) => s + (c.scores?.[i] || 0), 0) / others.length;
    const gap = avgComp - (myScores?.[i] || 0);
    if (gap > biggestGapVal) { biggestGapVal = gap; biggestGapDim = dim; }
  });

  const cards = [
    { icon: <Trophy size={20} />, iconBg: "#fef3c7", iconColor: "#f59e0b", label: "Leader Gap", value: `+${Math.max(0, leaderGap)} pts`, sub: "behind leader" },
    { icon: <BarChart3 size={20} />, iconBg: "#eff6ff", iconColor: "#3b82f6", label: "Your Rank", value: `#${yourRank} of ${competitors.length}`, sub: "in your area" },
    { icon: <Target size={20} />, iconBg: "#fef2f2", iconColor: "#ef4444", label: "Biggest Gap", value: biggestGapDim, sub: "vs competitors", locked: !hasAccess },
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10, marginTop: 16 }}>
      {cards.map((card, i) => (
        <div
          key={i}
          data-testid={`gap-card-${i}`}
          style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: "18px 16px",
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {card.locked && (
            <div style={{
              position: "absolute", inset: 0, background: "rgba(255,255,255,0.8)",
              backdropFilter: "blur(4px)", display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", zIndex: 2, borderRadius: 12,
            }}>
              <Lock size={16} style={{ color: "#dc2626", marginBottom: 4 }} />
              <p style={{ fontSize: 10, fontWeight: 700, color: "#374151", lineHeight: 1.4, maxWidth: 120 }}>
                See full competitor gap analysis
              </p>
              <button
                data-testid="gap-unlock-btn"
                onClick={onUnlock}
                style={{ fontSize: 10, fontWeight: 800, color: "#3b82f6", background: "none", border: "none", cursor: "pointer", marginTop: 4 }}
              >
                Unlock — $27
              </button>
            </div>
          )}
          <div style={{ width: 36, height: 36, borderRadius: 10, background: card.iconBg, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 10, color: card.iconColor }}>
            {card.icon}
          </div>
          <p style={{ fontSize: 11, fontWeight: 600, color: "#9ca3af", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>{card.label}</p>
          <p style={{ fontSize: 22, fontWeight: 800, color: "#111827", marginBottom: 2 }}>{card.value}</p>
          <p style={{ fontSize: 12, color: "#6b7280" }}>{card.sub}</p>
        </div>
      ))}
    </div>
  );
}

/* ═══ Main Component ═══ */
export default function CompetitorLeaderboard({ competitor_comparison, hasAccess, onUnlock, canonicalScore }) {
  if (!competitor_comparison?.competitors?.length) return null;

  const allEntries = competitor_comparison.competitors;
  const you = allEntries.find(c => c.is_you);
  // Use the canonical overall score (from scores.overall) as the single source of truth
  const myScore = canonicalScore != null ? canonicalScore : (you?.total || 0);
  const myScores = you?.scores || [];

  // Build a copy of entries where the user's total is the canonical score, so sorting/rank is consistent
  const normalizedEntries = allEntries.map(e => e.is_you ? { ...e, total: myScore } : e);

  // Sort by total score desc; find top non-you competitor for LEADER badge
  const sorted = [...normalizedEntries].sort((a, b) => b.total - a.total);
  const topNonYou = sorted.find(c => !c.is_you);
  let compRank = 0;

  return (
    <section id="competitors" data-testid="competitor-leaderboard" style={{ marginBottom: 40 }}>
      {/* Dark header */}
      <div className="report-section-pad" style={{
        background: "#0F172A",
        padding: "24px 24px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.35)", fontFamily: "'JetBrains Mono',monospace", letterSpacing: ".08em" }}>04</span>
          <div style={{ height: 1, flex: 1, background: "rgba(255,255,255,0.1)" }} />
        </div>
        <h2 className="report-section-head" style={{ fontSize: 20, fontWeight: 700, color: "#fff", marginBottom: 4, lineHeight: 1.3, fontFamily: "'Outfit',sans-serif" }}>
          How You Compare
        </h2>
        <p style={{ fontSize: 13, color: "#94A3B8", margin: 0, fontFamily: "'IBM Plex Sans',sans-serif" }}>
          Based on real Google data in your area
        </p>
      </div>

      {/* Leaderboard rows */}
      <div style={{
        background: "#fff",
        border: "1px solid #E2E8F0",
        borderTop: "none",
        overflow: "hidden",
        padding: "8px 0",
      }}>
        {sorted.map((entry, i) => {
          if (!entry.is_you) compRank++;
          return (
            <LeaderRow
              key={entry.place_id || entry.name}
              entry={entry}
              rank={entry.is_you ? null : compRank}
              myScore={myScore}
              index={i}
              isLeader={!entry.is_you && entry === topNonYou}
            />
          );
        })}
      </div>

      {/* Gap analysis cards */}
      <GapCards
        competitors={normalizedEntries}
        dimensions={competitor_comparison.dimensions}
        myScores={myScores}
        hasAccess={hasAccess}
        onUnlock={onUnlock}
      />

      {/* CSS keyframes */}
      <style>{`
        @keyframes pulseOnce {
          0% { box-shadow: 0 0 0 0 rgba(59,130,246,0.3); }
          50% { box-shadow: 0 0 0 8px rgba(59,130,246,0); }
          100% { box-shadow: 0 0 0 0 rgba(59,130,246,0); }
        }
      `}</style>
    </section>
  );
}
